import { QBO_PUSH_ERROR_MESSAGES, QBO_PUSH_ERRORS, QBO_PUSH_GENERIC_STEPS } from '../constants/lienConstants';
import type { LienData, LienBulkResultItem, LienBulkOutcome, PushBadgeState } from '../models/lienTypes';

/**
 * Case-insensitive `status === 'finalized'` check. The backend enum may
 * serialize as PascalCase ('Finalized'); the rest of the lien UI lowercases
 * status at each comparison, so we match that convention here.
 */
export function isFinalized(status: string | null | undefined): boolean {
  return String(status ?? '').toLowerCase() === 'finalized';
}

/** Case-insensitive `status === 'pushedToQbo'` check (mirrors `isFinalized`). */
export function isPushed(status: string | null | undefined): boolean {
  return String(status ?? '').toLowerCase() === 'pushedtoqbo';
}

// ── QBO push eligibility predicates ──────────────────────────────────────
//
// These are intentionally pure functions (no Vue reactivity) so they can be
// unit-tested in isolation and reused by both the Liens tab and the
// Settlement Calculator step.

/**
 * Derive the QBO push badge state from the consolidated status column. There
 * is no longer a stored push-status field: `pushedToQbo` means pushed; a
 * failed push leaves the lien Finalized with `qboPushError` populated;
 * anything else is not-yet-pushed.
 */
export function pushBadgeState(
  lien: Pick<LienData, 'status' | 'qboPushError'>,
): PushBadgeState {
  if (isPushed(lien.status)) return 'pushed';
  if (isFinalized(lien.status) && lien.qboPushError) return 'failed';
  return 'notPushed';
}

/** Finalized but held back because its amount exceeds the live trust balance. */
export function isPushBlocked(lien: Pick<LienData, 'status' | 'pushBlockedReason'>): boolean {
  return isFinalized(lien.status) && !!lien.pushBlockedReason;
}

/** A lien can be pushed to QBO when it is finalized, has no failed-push error, and isn't trust-blocked. */
export function canPushLien(
  lien: Pick<LienData, 'status' | 'qboPushError' | 'pushBlockedReason'>,
): boolean {
  return isFinalized(lien.status) && !lien.qboPushError && !isPushBlocked(lien);
}

/**
 * A lien push can be retried when the last attempt failed (still Finalized, with an error) — unless
 * trust is still short, in which case a retry would only fail the same way.
 */
export function canRetryLien(
  lien: Pick<LienData, 'status' | 'qboPushError' | 'pushBlockedReason'>,
): boolean {
  return isFinalized(lien.status) && !!lien.qboPushError && !isPushBlocked(lien);
}

/**
 * A lien push can be undone only when it is currently pushed AND (sc-46091) QBO doesn't report the
 * underlying check as Cleared/Reconciled. `canUndo` may be absent on older/partial fixtures — treat
 * that as "not proactively blocked" rather than false, so this stays additive.
 */
export function canUndoLien(lien: Pick<LienData, 'status' | 'canUndo'>): boolean {
  return isPushed(lien.status) && lien.canUndo !== false;
}

/** Eligible for the push/retry row action (pushable or in a failed state). */
export function isPushActionable(
  lien: Pick<LienData, 'status' | 'qboPushError' | 'pushBlockedReason'>,
): boolean {
  return canPushLien(lien) || canRetryLien(lien);
}

/**
 * True when at least one lien is finalized but not yet pushed to QBO.
 * Drives the non-blocking warning banner on Settlement Step 4. A pushed
 * lien's status is `pushedToQbo`, not `finalized`, so "finalized" alone now
 * means "not yet pushed" under the consolidated status column.
 */
export function hasUnpushedFinalizedLiens(
  liens: ReadonlyArray<Pick<LienData, 'status'>> | null | undefined,
): boolean {
  return liens?.some((l) => isFinalized(l.status)) ?? false;
}

/** Count of finalized-but-unpushed liens (for banner copy). */
export function countUnpushedFinalizedLiens(
  liens: ReadonlyArray<Pick<LienData, 'status'>> | null | undefined,
): number {
  return liens?.filter((l) => isFinalized(l.status)).length ?? 0;
}

// ── Service date range validation ─────────────────────────────────────────

export const SERVICE_END_BEFORE_START_ERROR =
  'Service end date cannot be before the start date.';

/**
 * Validates the optional service date range.
 * Returns an error message when `end` precedes `start`, otherwise null.
 * Same-day ranges (`end === start`) and null values are valid.
 *
 * Dates are compared as `YYYY-MM-DD` strings, which sort lexicographically
 * in chronological order — avoiding timezone drift from Date parsing.
 */
export function validateServiceDateRange(
  start: string | null | undefined,
  end: string | null | undefined,
): string | null {
  if (!start || !end) return null;
  const startDay = start.substring(0, 10);
  const endDay = end.substring(0, 10);
  return endDay < startDay ? SERVICE_END_BEFORE_START_ERROR : null;
}

// ── Error message resolution ──────────────────────────────────────────────

/**
 * Resolve a user-facing message for a QBO push error. The backend sends a
 * human-readable message in `error`/`qboPushError`; this also maps the small
 * set of known error keys (used by demo mode and any keyed responses).
 */
export function resolveQboPushError(
  errorOrKey: string | null | undefined,
  fallback?: string | null,
): string {
  if (errorOrKey && QBO_PUSH_ERROR_MESSAGES[errorOrKey]) {
    return QBO_PUSH_ERROR_MESSAGES[errorOrKey];
  }
  return errorOrKey || fallback || 'The QuickBooks operation could not be completed.';
}

/** Suggested resolution steps for a known QBO error key, else a generic list. */
export function resolveQboPushSteps(errorOrKey: string | null | undefined): string[] {
  if (errorOrKey && QBO_PUSH_ERRORS[errorOrKey]) {
    return QBO_PUSH_ERRORS[errorOrKey].steps;
  }
  return QBO_PUSH_GENERIC_STEPS;
}

/**
 * Derive a per-lien outcome from a backend bulk result row. Push rows report
 * `success` directly; undo rows report the resulting `status` instead (no
 * shared push-status field). DEAD CODE in production today (unit-tested only
 * — grepped, never called from app code): a real "skipped" undo row (never
 * pushed) and a real "failed" undo row both carry a non-null `error` and
 * can't be told apart from a single row in isolation, so this collapses both
 * to `'failed'`. If this is ever wired into UI, use the bulk response
 * envelope's `skipped`/`failed` totals instead of this per-row check.
 */
export function lienBulkOutcome(
  item: Pick<LienBulkResultItem, 'error' | 'success' | 'status'>,
): LienBulkOutcome {
  if (item.error) return 'failed';
  if (item.success || item.status) return 'success';
  return 'skipped';
}
